import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface ApiStats {
  totalCredits: number;
  limitCredits: number;
  activeUsers: number;
  apiStatuses: {
    openai: boolean;
    deepseek: boolean;
    googleVision: boolean;
    googleSearch: boolean;
  };
  consumptionByApi: {
    openai: { input: number; output: number; total: number };
    deepseek: { input: number; output: number; total: number };
    google: { input: number; output: number; total: number };
  };
  usagePercentage: number;
  periodInfo: {
    currentPeriod: string;
    startDate: string;
    endDate: string;
    nextRenewal: string;
    daysRemaining: number;
    daysInPeriod: number;
    periodProgress: number;
  };
}

export const useApiStats = () => {
  const [apiStats, setApiStats] = useState<ApiStats>({
    totalCredits: 0,
    limitCredits: 0,
    activeUsers: 0,
    apiStatuses: {
      openai: false,
      deepseek: false,
      googleVision: false,
      googleSearch: false,
    },
    consumptionByApi: {
      openai: { input: 0, output: 0, total: 0 },
      deepseek: { input: 0, output: 0, total: 0 },
      google: { input: 0, output: 0, total: 0 },
    },
    usagePercentage: 0,
    periodInfo: {
      currentPeriod: '',
      startDate: '',
      endDate: '',
      nextRenewal: '',
      daysRemaining: 0,
      daysInPeriod: 0,
      periodProgress: 0,
    },
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchApiStats = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Buscar dados de créditos dos usuários (mês atual)
      const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      
      // Calcular informações do período
      const daysInPeriod = endOfMonth.getDate();
      const daysRemaining = endOfMonth.getDate() - now.getDate() + 1;
      const daysPassed = now.getDate() - 1;
      const periodProgress = (daysPassed / daysInPeriod) * 100;
      
      const { data: creditsData, error: creditsError } = await supabase
        .from('user_credits')
        .select('credits_input, credits_output, total_credits, limit_credits')
        .eq('month_year', currentMonth);

      if (creditsError) throw creditsError;

      // Calcular totais de consumo
      const totalCreditsUsed = creditsData?.reduce((sum, user) => sum + (user.total_credits || 0), 0) || 0;
      const totalLimitCredits = creditsData?.reduce((sum, user) => sum + (user.limit_credits || 0), 0) || 0;

      // Buscar configurações das APIs
      const { data: apiSettings, error: apiError } = await supabase
        .from('api_settings')
        .select('openai_enabled, deepseek_enabled')
        .single();

      if (apiError && apiError.code !== 'PGRST116') throw apiError;

      // Buscar usuários ativos (últimos 2 minutos)
      const twoMinutesAgo = new Date(Date.now() - 2 * 60 * 1000).toISOString();
      
      const { data: activeUsersData, error: usersError } = await supabase
        .from('user_stats')
        .select('user_id')
        .eq('is_online', true)
        .gte('last_active', twoMinutesAgo);

      if (usersError) throw usersError;

      // Simular consumo por API (baseado em padrões de uso)
      const openaiConsumption = Math.floor(totalCreditsUsed * 0.7); // 70% OpenAI
      const deepseekConsumption = Math.floor(totalCreditsUsed * 0.2); // 20% DeepSeek
      const googleConsumption = totalCreditsUsed - openaiConsumption - deepseekConsumption; // Resto Google

      setApiStats({
        totalCredits: totalCreditsUsed,
        limitCredits: totalLimitCredits,
        activeUsers: activeUsersData?.length || 0,
        apiStatuses: {
          openai: apiSettings?.openai_enabled || false,
          deepseek: apiSettings?.deepseek_enabled || false,
          googleVision: true, // Google Vision é configurado via variáveis de ambiente
          googleSearch: true, // Assumir que Google Search está sempre ativo
        },
        consumptionByApi: {
          openai: {
            input: Math.floor(openaiConsumption * 0.4),
            output: Math.floor(openaiConsumption * 0.6),
            total: openaiConsumption,
          },
          deepseek: {
            input: Math.floor(deepseekConsumption * 0.4),
            output: Math.floor(deepseekConsumption * 0.6),
            total: deepseekConsumption,
          },
          google: {
            input: Math.floor(googleConsumption * 0.3),
            output: Math.floor(googleConsumption * 0.7),
            total: googleConsumption,
          },
        },
        usagePercentage: totalLimitCredits > 0 ? (totalCreditsUsed / totalLimitCredits) * 100 : 0,
        periodInfo: {
          currentPeriod: currentMonth,
          startDate: startOfMonth.toLocaleDateString('pt-BR'),
          endDate: endOfMonth.toLocaleDateString('pt-BR'),
          nextRenewal: nextMonth.toLocaleDateString('pt-BR'),
          daysRemaining,
          daysInPeriod,
          periodProgress: Math.min(periodProgress, 100),
        },
      });

    } catch (err) {
      console.error('Erro ao buscar estatísticas de API:', err);
      setError(err instanceof Error ? err.message : 'Erro desconhecido');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchApiStats();
    
    // Atualizar a cada 30 segundos
    const interval = setInterval(fetchApiStats, 30000);
    
    return () => clearInterval(interval);
  }, []);

  return {
    apiStats,
    isLoading,
    error,
    refetch: fetchApiStats,
  };
};